import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import { Send, RefreshCw, Sparkles, Copy, ArrowLeft, PenTool } from "lucide-react";
import { toast } from "sonner";

/**
 * /dashboard/writer-chat — "Mike's Writer" chat-style copywriter.
 *
 * Members type a request (rewrite this, give me 5 headlines for X, write me an
 * email about Y) and get copy back in Mike's voice. Multi-turn — they can keep
 * refining the same draft. Backed by Claude Sonnet 4.6 with a copywriter-specific
 * system prompt. History lives in `writer_chat_messages` server-side.
 */

const NAVY = "#0F172A";
const GOLD  = "#E8B539";
const WINE  = "#0F172A";
const PAPER = "#FFFFFF";

const STARTERS = [
  "Rewrite this in your voice: [paste your draft]",
  "Give me 5 short headlines for a Goli gummies email.",
  "Write a 3-line text follow-up to a chiropractor I just met.",
  "Write me a sales-page hook for the With Faith book.",
  "Turn this into a Facebook post — keep it under 280 characters: [paste]",
];

export default function WriterChatPage() {
  const { user } = useAuth();
  const [sid] = useState(() => {
    const k = "writer_chat_sid";
    let s = localStorage.getItem(k);
    if (!s) {
      s = "w_" + Math.random().toString(36).slice(2, 12);
      localStorage.setItem(k, s);
    }
    return s;
  });

  const [messages, setMessages] = useState([]);
  const [draft, setDraft]       = useState("");
  const [sending, setSending]   = useState(false);
  const scrollRef = useRef(null);
  const inputRef  = useRef(null);

  useEffect(() => {
    if (!user) return;
    api.get(`/chat/writer/history/${sid}`)
      .then((r) => setMessages(r.data?.messages || []))
      .catch(() => {});
  }, [user, sid]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  if (user === null) return <Navigate to="/login" replace />;
  if (user === undefined) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;

  const send = async (text) => {
    const msg = (text ?? draft).trim();
    if (!msg || sending) return;
    setDraft("");
    setMessages((m) => [...m, { id: "tmp-" + Date.now(), role: "user", text: msg, ts: new Date().toISOString() }]);
    setSending(true);
    try {
      const { data } = await api.post("/chat/writer/message", { session_id: sid, message: msg });
      setMessages((m) => [...m, { id: "r-" + Date.now(), role: "assistant", text: data.reply, ts: new Date().toISOString() }]);
    } catch (e) {
      setMessages((m) => [...m, { id: "e-" + Date.now(), role: "assistant", text: "Hmm — the AI Writer couldn't reach the engine right now. Try again in a minute.", ts: new Date().toISOString() }]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const reset = async () => {
    if (!confirm("Clear this writing session?")) return;
    await api.post(`/chat/writer/reset/${sid}`);
    setMessages([]);
  };

  const copyText = async (t) => {
    try { await navigator.clipboard.writeText(t); toast.success("Copied"); }
    catch { toast.error("Could not copy"); }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: PAPER }} data-testid="writer-chat-page">
      <SiteNav />

      <section className="flex-1 max-w-3xl w-full mx-auto px-5 md:px-8 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-start gap-3">
            <Link to="/dashboard/ai-writer"
                  data-testid="writer-chat-back-btn"
                  className="mt-1 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider hover:opacity-80"
                  style={{ color: WINE }}>
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </Link>
            <div>
              <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: "#2E9F3A" }}>
                ★ Chat With Our AI Writer
              </div>
              <h1 className="font-serif-display text-3xl md:text-4xl uppercase leading-tight mt-1"
                  style={{ color: NAVY }}>
                AI Writer
              </h1>
            </div>
          </div>
          {messages.length > 0 && (
            <button onClick={reset}
                    data-testid="writer-chat-reset-btn"
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border hover:bg-white"
                    style={{ borderColor: WINE, color: WINE }}>
              <RefreshCw className="w-3.5 h-3.5" /> Clear
            </button>
          )}
        </div>

        <p className="text-sm md:text-base italic mb-4" style={{ color: "#3D3D3D" }}>
          Talk back and forth with our AI Writer — paste a draft, ask for headlines, request an email, or brainstorm out loud. It writes in plain, Southern, common-man, kitchen-table voice.
        </p>

        <div ref={scrollRef}
             data-testid="writer-chat-messages"
             className="bg-white rounded-2xl border-2 p-4 md:p-5 space-y-3 overflow-y-auto"
             style={{ borderColor: "#E5E1D6", minHeight: 460, maxHeight: "62vh" }}>
          {messages.length === 0 && (
            <div className="text-center py-10">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-3"
                   style={{ background: "#FFFFFF", color: GOLD }}>
                <PenTool className="w-7 h-7" />
              </div>
              <div className="font-bold text-lg" style={{ color: NAVY }}>What do you want to write?</div>
              <div className="text-sm mt-1" style={{ color: "#5A5A5A" }}>
                Paste a draft, ask for headlines, or describe what you need.
              </div>
              <div className="mt-6 grid sm:grid-cols-1 gap-2 max-w-xl mx-auto text-left">
                {STARTERS.map((s, i) => (
                  <button key={i}
                          data-testid={`writer-starter-${i}`}
                          onClick={() => send(s)}
                          className="text-left text-sm px-3 py-2.5 rounded-lg border-2 hover:shadow-md hover:-translate-y-0.5 transition bg-white"
                          style={{ borderColor: "#E5E1D6", color: "#0F172A" }}>
                    <Sparkles className="inline w-3.5 h-3.5 mr-1.5" style={{ color: GOLD }} />
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id}
                 data-testid={`writer-msg-${m.role}`}
                 className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} group`}>
              <div className={`max-w-[92%] rounded-2xl px-4 py-3 text-[15px] leading-relaxed whitespace-pre-wrap shadow-sm relative`}
                   style={ m.role === "user"
                     ? { background: "#FFFFFF", color: "#FFFFFF" }
                     : { background: "#FFFFFF", color: "#0F172A", border: `1px solid ${GOLD}55` } }>
                {m.text}
                {m.role === "assistant" && (
                  <button onClick={() => copyText(m.text)}
                          data-testid="writer-copy-btn"
                          className="absolute -bottom-2 right-3 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition shadow"
                          style={{ background: GOLD, color: NAVY }}>
                    <Copy className="inline w-3 h-3 mr-1" /> Copy
                  </button>
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start" data-testid="writer-typing">
              <div className="rounded-2xl px-4 py-2.5 text-sm shadow-sm"
                   style={{ background: "#FFFFFF", border: `1px solid ${GOLD}55`, color: "#5A5A5A" }}>
                <span className="inline-block animate-pulse">Writing…</span>
              </div>
            </div>
          )}
        </div>

        <form className="mt-3 flex gap-2 items-end"
              data-testid="writer-chat-form"
              onSubmit={(e) => { e.preventDefault(); send(); }}>
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Type a request or paste a draft. Shift+Enter for new line…"
            rows={2}
            maxLength={12000}
            data-testid="writer-chat-input"
            disabled={sending}
            className="flex-1 px-4 py-3 rounded-2xl border-2 text-base outline-none focus:border-[#E8B539] resize-none"
            style={{ borderColor: "#D6D2C9", background: "#FFFFFF" }}
          />
          <button
            type="submit"
            disabled={sending || !draft.trim()}
            data-testid="writer-chat-send-btn"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl text-sm font-bold uppercase tracking-wider shadow hover:scale-[1.03] transition disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: GOLD, color: NAVY, border: `2px solid ${NAVY}` }}>
            <Send className="w-4 h-4" /> Send
          </button>
        </form>

        <div className="text-[11px] mt-3 text-center" style={{ color: "#7A7066" }}>
          The AI Writer matches our voice as best it can — read it before you send it. AI can make mistakes.
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
